import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import {
  InvalidUserUnauthorized,
  InvalidUserWrongAccountUsed,
} from 'api/kinerja/useAPIKinerja'
import {
  InvalidUserNoAccess,
  InvalidUserNoAccessPelaksanaan,
  PenetapanNotAllowed,
} from 'api/kinerja/kepala-sekolah/config'
import {
  PANDUAN_PENGELOLAAN_KENDALA_AKSES,
  PANDUAN_PENGELOLAAN_URL,
} from 'app/KinerjaShared/utils/panduan'
import { ForbiddenError, NotFoundError } from 'utils/customError'
import {
  PENGELOLAAN_KINERJA_KADIN_PATH,
  PENGELOLAAN_KINERJA_PS_PATH,
} from 'app/KinerjaShared/constants/path'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'

import type { TKinerjaErrorAction } from 'app/KinerjaShared/components/KinerjaErrorBoundary/KinerjaError'

import ImgFence from 'assets/icons/img-fence.svg'

export type TKinerjaError = {
  errorType: any
  title?: string
  icon?: any
  ctaAction?: TKinerjaErrorAction
  secondaryCtaAction?: TKinerjaErrorAction
  children?: (error: any) => JSX.Element
}

export function useKinerjaError() {
  const router = useRouter()
  const { isKepalaDinasAndPengawasSekolah } = useRoleContext()

  const ERRORS: TKinerjaError[] = [
    {
      errorType: InvalidUserUnauthorized,
      title: 'Saat ini, halaman baru tersedia untuk Guru dan Kepala Sekolah',
      icon: ImgFence,
      ctaAction: {
        text: 'Hubungi Kami',
        action: () => {
          window.open(PANDUAN_PENGELOLAAN_KENDALA_AKSES, '_blank')
        },
      },
      children: (_error: any) => (
        <>
          <Text variant="body-lg" color="subdued">
            Pastikan data “Jenis PTK” di Dapodik adalah Guru atau Kepala
            Sekolah. Mohon hubungi operator sekolah untuk pembaruan data diri
            Anda di Dapodik.
          </Text>
        </>
      ),
    },
    {
      errorType: InvalidUserWrongAccountUsed,
      title: 'Masuk ke akun Anda yang lain',
      icon: ImgFence,
      ctaAction: {
        text: 'Masuk ke akun',
        action: () => {
          const query = new URLSearchParams()
          query.set('from', window.location.pathname)
          window.location.href = `/logout?target=/login?${query.toString()}`
        },
      },
      secondaryCtaAction: {
        text: 'Kembali',
        action: () => {
          router.back()
        },
      },
      children: (error: any) => (
        <>
          <Text variant="body-lg" color="subdued">
            Saat ini akun tidak memiliki akses ke Pengelolaan Kinerja. <br />
            Untuk lanjut, mohon masuk ke akun:
            <br />
            <strong>
              {(error as InvalidUserWrongAccountUsed).alternateEmail}
            </strong>
            .
          </Text>

          <div className="mt-4">
            <Button
              variant="ghost"
              as="a"
              href={PANDUAN_PENGELOLAAN_URL}
              target="_blank"
              rel="noreferrer"
              className="!inline-flex gap-1 !pl-0"
            >
              <Icon fontSize="sm" color="informational">
                help
              </Icon>
              Hubungi Kami
            </Button>
          </div>
        </>
      ),
    },
    {
      errorType: InvalidUserNoAccess,
      title:
        'Saat ini halaman baru tersedia untuk Guru, Kepala Sekolah, Tim Kerja (Pengawas Sekolah), dan Dinas Pendidikan',
      icon: ImgFence,
      ctaAction: {
        text: 'Kembali',
        action: () => {
          router.back()
        },
      },
      secondaryCtaAction: {
        text: (
          <>
            <Icon fontSize="sm" color="informational">
              help
            </Icon>{' '}
            Hubungi Kami
          </>
        ),
        className: '!inline-flex gap-2',
        action: () => {
          window.open(PANDUAN_PENGELOLAAN_URL, '_blank')
        },
      },
      children: (error: any) => (
        <Text variant="body-lg" color="subdued">
          {error?.description}
        </Text>
      ),
    },
    {
      errorType: InvalidUserNoAccessPelaksanaan,
      title: 'Halaman belum tersedia untuk Anda',
      icon: ImgFence,
      ctaAction: {
        text: 'Kembali',
        action: () => {
          router.back()
        },
      },
      secondaryCtaAction: {
        text: (
          <>
            <Icon fontSize="sm" color="informational">
              help
            </Icon>{' '}
            Hubungi Kami
          </>
        ),
        className: '!inline-flex gap-2',
        action: () => {
          window.open(PANDUAN_PENGELOLAAN_URL, '_blank')
        },
      },
      children: (error: any) => (
        <Text variant="body-lg" color="subdued">
          {error?.description}
        </Text>
      ),
    },
    {
      errorType: NotFoundError,
      title: 'Halaman tidak ditemukan',
      icon: ImgFence,
      ctaAction: {
        text: 'Kembali',
        action: () => {
          router.back()
        },
      },
      secondaryCtaAction: {
        text: (
          <>
            <Icon fontSize="sm" color="informational">
              help
            </Icon>{' '}
            Hubungi Kami
          </>
        ),
        className: '!inline-flex gap-2',
        action: () => {
          window.open(PANDUAN_PENGELOLAAN_URL, '_blank')
        },
      },
      children: (error: any) => (
        <Text variant="body-lg" color="subdued">
          {error?.description}
        </Text>
      ),
    },
    {
      errorType: ForbiddenError,
      icon: ImgFence,
      ctaAction: {
        text: 'Kembali',
        action: () => {
          router.back()
        },
      },
      secondaryCtaAction: {
        text: (
          <>
            <Icon fontSize="sm" color="informational">
              help
            </Icon>{' '}
            Hubungi Kami
          </>
        ),
        className: '!inline-flex gap-2',
        action: () => {
          window.open(PANDUAN_PENGELOLAAN_URL, '_blank')
        },
      },
      children: (error: any) => (
        <Text variant="body-lg" color="subdued">
          {error?.description}
        </Text>
      ),
    },
    {
      errorType: PenetapanNotAllowed,
      icon: ImgFence,
      ctaAction: {
        text: 'Kembali',
        action: () => {
          router.push(
            isKepalaDinasAndPengawasSekolah
              ? PENGELOLAAN_KINERJA_KADIN_PATH
              : PENGELOLAAN_KINERJA_PS_PATH
          )
        },
      },
      secondaryCtaAction: {
        text: (
          <>
            <Icon fontSize="sm" color="informational">
              help
            </Icon>{' '}
            Hubungi Kami
          </>
        ),
        className: '!inline-flex gap-2',
        action: () => {
          window.open(PANDUAN_PENGELOLAAN_URL, '_blank')
        },
      },
    },
  ]

  return { ERRORS }
}
